import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { RadioGroup, Modal, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { navigateToEditor, navigateToPDF, navigateToPresentation } from 'router/navigation';
import { toast } from 'utils/toast';

import { closeAndResetModal } from '_common/modals/ModalsSlice';
import { convertFileTo, setAppLoading } from 'App/redux/appSlice';

import styles from './ConvertToModal.module.scss';
import { FILE_TYPE_FROM_MIME } from '_common/consts';
import ObjectApi from '_common/services/api/ObjectApi';
import { InstanceService } from '_common/services';
import { getValidZipMime } from 'utils';

function isFile(object: any): object is doDOC.File {
  return object.type && object.type === 'file';
}

const MODAL = 'ConvertToModal';

type ConvertAction = 'keep' | 'remove';

const ConvertToModal = () => {
  const dispatch = useDispatch();

  const objectStatusTimeout = useRef<number>(undefined);

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const objectIds = useSelector((state) => state.modals[MODAL].objectIds);
  const openAfterConverting = useSelector((state) => state.modals[MODAL].openAfterConverting);

  const [action, setAction] = useState<ConvertAction>('keep');
  const [objectsData, setObjectsData] =
    useState<Record<ObjectId, { type: 'dopdf' | 'document' | 'presentation'; name: string }>>();

  useEffect(() => {
    const fetchData = async () => {
      const data: typeof objectsData = {};
      for (const i in objectIds) {
        try {
          const { data: objectData } = await dispatch(
            ObjectApi.endpoints.getObject2.initiate({
              object_id: objectIds[i],
              object_type: 'file',
            }),
          );

          const validMimeType = getValidZipMime(objectData as Objekt);

          if (objectData && isFile(objectData)) {
            data[objectIds[i]] = {
              type:
                validMimeType || objectData.mime?.type
                  ? // @ts-expect-error can't restrict mime type TS type
                    FILE_TYPE_FROM_MIME[validMimeType || objectData.mime.type]
                  : 'document',
              name: objectData.name,
            };
          }
        } catch (e) {
          throw e;
        }
      }
      setObjectsData(data);
    };

    fetchData();
  }, [objectIds]);

  const objectsType = useMemo<'dopdf' | 'document' | 'presentation' | 'multi' | null>(() => {
    let type: 'dopdf' | 'document' | 'presentation' | 'multi' | null = null;
    if (objectsData) {
      Object.keys(objectsData).forEach((key) => {
        if (!type) {
          type = objectsData[key].type;
        } else if (type !== objectsData[key].type) {
          type = 'multi';
        }
      });
    }
    return type;
  }, [objectsData]);

  const handleObjectProcessing = async (object: doDOC.BaseObject) => {
    const { status, id, type } = object;

    return new Promise((resolve) => {
      if (status === 'processing') {
        objectStatusTimeout.current = window.setInterval(async () => {
          const { data } = await new InstanceService().getObjectStatus(type, id);
          if (data.status !== 'processing') {
            resolve(data);
            clearInterval(objectStatusTimeout.current);
          }
        }, 5000);
      }
    });
  };

  const submit = async () => {
    if (objectsData) {
      close();
      dispatch(
        setAppLoading({
          isOpen: true,
          message: 'CONVERTING_FILE',
          messageValues: { length: objectIds.length },
        }),
      );
      Promise.all(
        objectIds.map((objectId: string) =>
          dispatch(
            convertFileTo({
              objectId,
              deleteSource: action === 'remove',
              objectType: objectsData?.[objectId].type,
            }),
          ),
        ),
      ).then(async (result) => {
        if (openAfterConverting && result.length === 1) {
          const convertedObject = result[0].payload;

          if (convertedObject.status === 'processing') {
            await handleObjectProcessing(convertedObject);
          }
          if (convertedObject.type === 'document') {
            navigateToEditor(convertedObject.id, true);
          } else if (objectsType === 'dopdf') {
            navigateToPDF(convertedObject.id, true);
          } else if (objectsType === 'presentation') {
            navigateToPresentation(convertedObject.id, true);
          }
        }

        dispatch(setAppLoading({ isOpen: false }));

        toast({
          variant: 'success',
          title: 'FILE_CONVERTED',
          titleValues: { length: objectIds.length },
          message: 'CONVERTED_FILE_MESSAGE',
          messageValues: {
            newFileType:
              objectsType === 'dopdf'
                ? 'doDOC PDF'
                : objectsType === 'presentation'
                  ? 'doPPT'
                  : 'doDOC',
            length: objectIds.length,
          },
        });
      });
    }
  };

  const close = () => {
    dispatch(closeAndResetModal(MODAL));
  };

  return (
    <Modal open={!!isOpen} close={close} testId="convert-to" width="60rem">
      <Modal.Header>
        <FormattedMessage
          id="CONVERT_FILE_TO_MODAL_TITLE"
          values={{
            newFileType:
              objectsType === 'dopdf' ? 'doPDF' : objectsType === 'presentation' ? 'doPPT' : '.ddc',
            length: objectIds.length,
          }}
        />
      </Modal.Header>
      <Modal.Body>
        <div className={styles.description}>
          <FormattedMessage
            id="CONVERT_FILE_TO_MODAL_DESCRIPTION"
            values={{
              fileTitle: objectsData?.[objectIds[0]]?.name,
              newFileType:
                objectsType === 'dopdf'
                  ? 'doPDF'
                  : objectsType === 'presentation'
                    ? 'doPPT'
                    : '.ddc',
              length: objectIds.length,
            }}
          />
        </div>
        <div className={styles.actions}>
          <RadioGroup
            items={[
              {
                label: (
                  <FormattedMessage id="KEEP_ORIGINAL_FILE" values={{ length: objectIds.length }} />
                ),
                value: 'keep',
              },
              {
                label: (
                  <FormattedMessage
                    id="REMOVE_ORIGINAL_FILE"
                    values={{ length: objectIds.length }}
                  />
                ),
                value: 'remove',
              },
            ]}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            testId="convert-to-modal"
            direction="vertical"
            size="xs"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="standard" size="m" onClick={close} testId="convert-to-modal-close">
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          variant="primary"
          size="m"
          style={{ margin: '0 0 0 1rem' }}
          onClick={submit}
          testId="convert-to-modal-submit"
        >
          <FormattedMessage id="CONVERT" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConvertToModal;
